// src/components/UnreviewedPackets.jsx
import React, { useMemo, useState, useCallback } from "react";
import {
  Box,
  Paper,
  Typography,
  Stack,
  Button,
  Chip,
  Divider,
  Alert,
  TextField,
  MenuItem,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import { DataGrid } from "@mui/x-data-grid";
import { RefreshCw, CheckCircle2 } from "lucide-react";

import {
  useGetUnreviewedPacketsQuery,
  useLazyAnalyzePacketQuery,
  useReviewPacketMutation,
  useGetProtocolsQuery,
} from "../../redux/protocol/protocolApi";

export default function UnreviewedPackets() {
  const theme = useTheme();

  // ✅ DataGrid v5 pagination state (server)
  const [page, setPage] = useState(0); // 0-based for DataGrid
  const [pageSize, setPageSize] = useState(10);

  // ✅ selection
  const [selectedId, setSelectedId] = useState(null);

  // ✅ review form
  const [reviewForm, setReviewForm] = useState({ notes: "", protocolId: "" });

  // ✅ API (your backend expects 1-based page)
  const { data: packetsData, isLoading, error, refetch } = useGetUnreviewedPacketsQuery({
    page: page + 1,
    limit: pageSize,
  });

  const { data: protocolsData } = useGetProtocolsQuery({ status: "active" });

  const [analyzePacket, { data: analysisData, isFetching: isAnalyzing }] =
    useLazyAnalyzePacketQuery();

  const [reviewPacket, { isLoading: isReviewing }] = useReviewPacketMutation();

  const packets = packetsData?.data || [];
  const pagination = packetsData?.pagination; // { total, pages, page, limit } expected
  const protocols = protocolsData?.data || [];
  const analysis = analysisData?.data;

  // ✅ rows
  const rows = useMemo(() => {
    return (packets || []).map((p) => ({
      id: p._id,
      remoteAddress: p.remoteAddress || "-",
      packetLength: p.packetLength ?? 0,
      repeatCount: p.repeatCount ?? 0,
      createdAt: p.createdAt,
      header: p.detectedFingerprint?.header || "N/A",
      patternType: p.detectedFingerprint?.patternType || "unknown",
      rawHex: p.rawHex,
      _raw: p,
    }));
  }, [packets]);

  // ✅ columns (v5 safe formatters)
  const columns = useMemo(
    () => [
      { field: "remoteAddress", headerName: "IP", flex: 1, minWidth: 160 },
      {
        field: "packetLength",
        headerName: "Size",
        width: 110,
        valueFormatter: (params) => `${params?.value ?? 0}B`,
      },
      {
        field: "header",
        headerName: "Header",
        width: 160,
        renderCell: (params) => (
          <Chip
            size="small"
            label={params.value}
            sx={{
              fontWeight: 900,
              borderRadius: 1.6,
              bgcolor: alpha(theme.palette.info.main, 0.08),
            }}
          />
        ),
      },
      {
        field: "patternType",
        headerName: "Type",
        width: 140,
        renderCell: (params) => (
          <Chip
            size="small"
            label={params.value}
            variant="outlined"
            sx={{ fontWeight: 900 }}
          />
        ),
      },
      { field: "repeatCount", headerName: "Repeats", width: 110 },
      {
        field: "createdAt",
        headerName: "Created",
        flex: 1,
        minWidth: 190,
        valueGetter: (params) =>
          params?.row?.createdAt
            ? new Date(params.row.createdAt).toLocaleString()
            : "-",
      },
    ],
    [theme]
  );

  const selectedRow = useMemo(() => {
    return rows.find((r) => r.id === selectedId) || null;
  }, [rows, selectedId]);

  const selectRow = useCallback(
    async (id) => {
      setSelectedId(id || null);
      const row = rows.find((r) => r.id === id);
      if (row?._raw?._id) {
        analyzePacket(row._raw._id);
      }
    },
    [rows, analyzePacket]
  );

  const handleReview = async () => {
    if (!selectedRow?._raw?._id) return;

    try {
      await reviewPacket({
        packetId: selectedRow._raw._id,
        notes: reviewForm.notes,
        protocolId: reviewForm.protocolId || undefined,
      }).unwrap();

      setSelectedId(null);
      setReviewForm({ notes: "", protocolId: "" });
      refetch();
    } catch (err) {
      alert("Error: " + (err?.data?.error || err?.message || "Review failed"));
    }
  };

  const rowCount = pagination?.total ?? rows.length;
  const totalPages = pagination?.pages ?? 1;

  return (
    <Box>
      {/* Top bar */}
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={1.2}
        alignItems={{ sm: "center" }}
        justifyContent="space-between"
        sx={{ mb: 2 }}
      >
        <Box>
          <Typography sx={{ fontWeight: 900, fontSize: 18 }}>
            Unreviewed Packets
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Select a row to analyze. Mark it reviewed when done.
          </Typography>
        </Box>

        <Button
          onClick={() => refetch()}
          variant="outlined"
          startIcon={<RefreshCw size={16} />}
          sx={{ borderRadius: 2, fontWeight: 900, textTransform: "none" }}
        >
          Refresh
        </Button>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error?.data?.error || "Failed to load packets"}
        </Alert>
      )}

      {/* Split layout */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", lg: "1.2fr 1fr" },
          gap: 1.5,
          alignItems: "start",
        }}
      >
        {/* LEFT: list */}
        <Paper variant="outlined" sx={{ borderRadius: 3, overflow: "hidden" }}>
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            justifyContent="space-between"
            sx={{ px: 2, py: 1.5 }}
          >
            <Typography sx={{ fontWeight: 900 }}>
              Packets{" "}
              <Typography component="span" color="text.secondary">
                ({rowCount})
              </Typography>
            </Typography>

            <Chip
              size="small"
              label={`Page ${page + 1} / ${totalPages}`}
              variant="outlined"
              sx={{ fontWeight: 900 }}
            />
          </Stack>
          <Divider />

          <Box sx={{ height: 520 }}>
            <DataGrid
              rows={rows || []}
              columns={columns}
              loading={isLoading}
              disableSelectionOnClick
              // ✅ v5 selection
              selectionModel={selectedId ? [selectedId] : []}
              onSelectionModelChange={(m) => selectRow(m?.[0] || null)}
              // ✅ v5 server pagination props
              pagination
              paginationMode="server"
              rowCount={rowCount}
              page={page}
              onPageChange={(newPage) => setPage(newPage)}
              pageSize={pageSize}
              onPageSizeChange={(newSize) => {
                setPageSize(newSize);
                setPage(0);
              }}
              rowsPerPageOptions={[10, 20, 50]}
              sx={{
                border: "none",
                "& .MuiDataGrid-columnHeaders": {
                  bgcolor: alpha(theme.palette.primary.main, 0.04),
                  fontWeight: 900,
                },
                "& .MuiDataGrid-row:hover": {
                  bgcolor: alpha(theme.palette.primary.main, 0.03),
                },
                "& .MuiDataGrid-cell": { outline: "none" },
              }}
            />
          </Box>
        </Paper>

        {/* RIGHT: analysis */}
        <Paper variant="outlined" sx={{ borderRadius: 3, overflow: "hidden" }}>
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            justifyContent="space-between"
            sx={{ px: 2, py: 1.5 }}
          >
            <Typography sx={{ fontWeight: 900 }}>Analysis</Typography>
            {selectedRow ? (
              <Chip
                size="small"
                label={`Selected: ${selectedRow.header}`}
                variant="outlined"
                sx={{ fontWeight: 900 }}
              />
            ) : (
              <Chip
                size="small"
                label="No selection"
                variant="outlined"
                sx={{ fontWeight: 900 }}
              />
            )}
          </Stack>
          <Divider />

          <Box sx={{ p: 2 }}>
            {!selectedRow ? (
              <Alert severity="info">Select a packet from the table to analyze.</Alert>
            ) : isAnalyzing ? (
              <Alert severity="info">Analyzing packet…</Alert>
            ) : !analysis ? (
              <Alert severity="warning">Failed to analyze packet.</Alert>
            ) : (
              <Stack spacing={2}>
                {/* Hex */}
                <Box>
                  <Typography sx={{ fontWeight: 900, mb: 0.8 }}>Hex Data</Typography>
                  <Box
                    component="pre"
                    sx={{
                      m: 0,
                      p: 1.5,
                      borderRadius: 2,
                      fontSize: 12,
                      overflow: "auto",
                      bgcolor: alpha(theme.palette.text.primary, 0.04),
                      border: `1px solid ${alpha(theme.palette.divider, 0.9)}`,
                      fontFamily:
                        "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
                      whiteSpace: "pre-wrap",
                      wordBreak: "break-all",
                    }}
                  >
                    {analysis.analysis?.hex || selectedRow.rawHex || "N/A"}
                  </Box>
                </Box>

                {/* ASCII */}
                <Box>
                  <Typography sx={{ fontWeight: 900, mb: 0.8 }}>ASCII</Typography>
                  <Box
                    component="pre"
                    sx={{
                      m: 0,
                      p: 1.5,
                      borderRadius: 2,
                      fontSize: 12,
                      overflow: "auto",
                      bgcolor: alpha(theme.palette.text.primary, 0.04),
                      border: `1px solid ${alpha(theme.palette.divider, 0.9)}`,
                      fontFamily:
                        "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
                      whiteSpace: "pre-wrap",
                      wordBreak: "break-word",
                    }}
                  >
                    {analysis.analysis?.ascii || "N/A"}
                  </Box>
                </Box>

                {/* Potential IMEIs */}
                {analysis.potentialIMEIs?.length > 0 && (
                  <Box>
                    <Typography sx={{ fontWeight: 900, mb: 0.8 }}>
                      Potential IMEIs
                    </Typography>
                    <Stack spacing={0.8}>
                      {analysis.potentialIMEIs.map((it, i) => (
                        <Paper
                          key={i}
                          variant="outlined"
                          sx={{
                            p: 1.2,
                            borderRadius: 2,
                            borderColor: alpha(theme.palette.divider, 0.9),
                          }}
                        >
                          <Stack
                            direction="row"
                            spacing={1}
                            alignItems="center"
                            justifyContent="space-between"
                          >
                            <Typography sx={{ fontWeight: 900 }}>{it.imei}</Typography>
                            <Chip
                              size="small"
                              label={`${it.encoding} · offset ${it.offset}`}
                              variant="outlined"
                              sx={{ fontWeight: 900 }}
                            />
                          </Stack>
                        </Paper>
                      ))}
                    </Stack>
                  </Box>
                )}

                {/* Byte breakdown */}
                {analysis.analysis?.breakdown && (
                  <Box>
                    <Typography sx={{ fontWeight: 900, mb: 0.8 }}>
                      Byte Breakdown (first 32 bytes)
                    </Typography>
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: "repeat(8, 1fr)",
                        gap: 0.7,
                      }}
                    >
                      {analysis.analysis.breakdown.slice(0, 32).map((b, i) => (
                        <Paper
                          key={i}
                          variant="outlined"
                          title={`Offset: ${b.offset}, Dec: ${b.dec}`}
                          sx={{
                            p: 0.8,
                            borderRadius: 2,
                            textAlign: "center",
                            borderColor: alpha(theme.palette.divider, 0.9),
                          }}
                        >
                          <Typography
                            sx={{
                              fontWeight: 900,
                              fontSize: 12,
                              color: theme.palette.primary.main,
                            }}
                          >
                            {b.hex}
                          </Typography>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ fontWeight: 800 }}
                          >
                            {b.ascii}
                          </Typography>
                        </Paper>
                      ))}
                    </Box>
                  </Box>
                )}

                {/* Review */}
                <Divider />
                <Box>
                  <Typography sx={{ fontWeight: 900, mb: 1 }}>
                    Mark as Reviewed
                  </Typography>

                  <Stack spacing={1.2}>
                    <TextField
                      select
                      label="Match to Protocol"
                      size="small"
                      value={reviewForm.protocolId}
                      onChange={(e) =>
                        setReviewForm((p) => ({ ...p, protocolId: e.target.value }))
                      }
                    >
                      <MenuItem value="">-- No match / Unknown --</MenuItem>
                      {protocols.map((p) => (
                        <MenuItem key={p._id} value={p.protocolId}>
                          {p.name} ({p.protocolId})
                        </MenuItem>
                      ))}
                    </TextField>

                    <TextField
                      label="Notes"
                      size="small"
                      value={reviewForm.notes}
                      onChange={(e) =>
                        setReviewForm((p) => ({ ...p, notes: e.target.value }))
                      }
                      multiline
                      minRows={2}
                      placeholder="Add review notes…"
                    />

                    <Stack direction="row" justifyContent="flex-end">
                      <Button
                        variant="contained"
                        onClick={handleReview}
                        disabled={isReviewing}
                        startIcon={<CheckCircle2 size={16} />}
                        sx={{
                          borderRadius: 2,
                          fontWeight: 900,
                          textTransform: "none",
                        }}
                      >
                        {isReviewing ? "Saving..." : "Mark as Reviewed"}
                      </Button>
                    </Stack>
                  </Stack>
                </Box>
              </Stack>
            )}
          </Box>
        </Paper>
      </Box>
    </Box>
  );
}
